import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { colors, typography, spacing } from '../../theme';
import { useToast } from '../Toast';

export const AgentMode: React.FC = () => {
  const { t } = useTranslation();
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const toast = useToast();

  const handleToggleAgent = () => {
    if (extensionInstalled) {
      setAgentEnabled(!agentEnabled);
    } else {
      toast.warning('Please install the Chrome Extension first!');
    }
  };

  return (
    <Container>
      <Header>
        <Title>{t('agent.title')}</Title>
        <Subtitle>
          {t('agent.subtitle')}
        </Subtitle>
      </Header>

      {/* Extension Status Card */}
      <StatusCard>
        <StatusHeader>
          <StatusIcon installed={extensionInstalled}>
            {extensionInstalled ? '✓' : '⚠'}
          </StatusIcon>
          <StatusInfo>
            <StatusTitle>{t('agent.browserExtension')}</StatusTitle>
            <StatusText installed={extensionInstalled}>
              {extensionInstalled ? t('agent.installed') : t('agent.notInstalled')}
            </StatusText>
          </StatusInfo>
        </StatusHeader>

        {!extensionInstalled && (
          <InstallButton
            onClick={() => setExtensionInstalled(true)}
            aria-label="Install Chrome Extension for DIDI"
          >
            <span aria-hidden="true">📦</span> {t('agent.installExtension')}
          </InstallButton>
        )}
      </StatusCard>

      {/* Agent Toggle */}
      <ControlCard>
        <ControlHeader>
          <ControlTitle>
            <AgentIcon aria-hidden="true">🤖</AgentIcon>
            {t('agent.agentStatus')}
          </ControlTitle>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={agentEnabled}
              onChange={handleToggleAgent}
              disabled={!extensionInstalled}
              aria-label="Enable or disable AI agent monitoring"
              aria-checked={agentEnabled}
            />
            <ToggleSlider enabled={agentEnabled} aria-hidden="true" />
          </ToggleSwitch>
        </ControlHeader>
        <ControlDescription>
          {agentEnabled
            ? `✅ ${t('agent.agentActive')}`
            : `⏸️ ${t('agent.agentPaused')}`}
        </ControlDescription>
      </ControlCard>

      {/* Guide Section */}
      <GuideSection>
        <GuideHeader>
          <GuideTitle>📹 {t('agent.howToUse')}</GuideTitle>
          <GuideSubtitle>
            {t('agent.watchGuide')}
          </GuideSubtitle>
        </GuideHeader>

        <VideoGuide>
          <VideoPlaceholder>
            <PlayIcon>▶️</PlayIcon>
            <VideoTitle>{t('agent.videoTitle')}</VideoTitle>
            <VideoDescription>
              {t('agent.videoDesc')}
            </VideoDescription>
          </VideoPlaceholder>
        </VideoGuide>

        <GuideSteps>
          <Step>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>{t('agent.step1Title')}</StepTitle>
              <StepText>{t('agent.step1Desc')}</StepText>
            </StepContent>
          </Step>

          <Step>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>{t('agent.step2Title')}</StepTitle>
              <StepText>{t('agent.step2Desc')}</StepText>
            </StepContent>
          </Step>

          <Step>
            <StepNumber>3</StepNumber>
            <StepContent>
              <StepTitle>{t('agent.step3Title')}</StepTitle>
              <StepText>
                {t('agent.step3Desc')}
              </StepText>
            </StepContent>
          </Step>

          <Step>
            <StepNumber>4</StepNumber>
            <StepContent>
              <StepTitle>{t('agent.step4Title')}</StepTitle>
              <StepText>{t('agent.step4Desc')}</StepText>
            </StepContent>
          </Step>
        </GuideSteps>
      </GuideSection>

      {/* Activity Log */}
      {agentEnabled && (
        <ActivitySection>
          <ActivityHeader>
            <ActivityTitle>{t('agent.recentActivity')}</ActivityTitle>
            <ActivityBadge>{t('agent.live')}</ActivityBadge>
          </ActivityHeader>

          <ActivityList>
            <ActivityItem>
              <ActivityIcon>🔍</ActivityIcon>
              <ActivityInfo>
                <ActivityText>Analyzed article on Twitter</ActivityText>
                <ActivityTime>2 minutes ago</ActivityTime>
              </ActivityInfo>
              <ActivityResult type="safe">✓ Verified</ActivityResult>
            </ActivityItem>

            <ActivityItem>
              <ActivityIcon>⚠️</ActivityIcon>
              <ActivityInfo>
                <ActivityText>Detected suspicious claim on Facebook</ActivityText>
                <ActivityTime>15 minutes ago</ActivityTime>
              </ActivityInfo>
              <ActivityResult type="warning">⚠ Flagged</ActivityResult>
            </ActivityItem>

            <ActivityItem>
              <ActivityIcon>🔍</ActivityIcon>
              <ActivityInfo>
                <ActivityText>Checked news headline</ActivityText>
                <ActivityTime>1 hour ago</ActivityTime>
              </ActivityInfo>
              <ActivityResult type="safe">✓ Verified</ActivityResult>
            </ActivityItem>
          </ActivityList>
        </ActivitySection>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 0;
  @media (max-width: 768px) { padding: 0; }
`;

const Header = styled.div`
  margin-bottom: ${spacing['3xl']}px;
`;

const Title = styled.h2`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.softWhite};
  margin-bottom: ${spacing.md}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const Subtitle = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  color: ${colors.steelGray};
  line-height: ${typography.lineHeight.relaxed};
`;

const StatusCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 191, 166, 0.2);
  border-radius: 16px;
  padding: ${spacing['2xl']}px;
  margin-bottom: ${spacing.xl}px;

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.02);
    border-color: rgba(139, 92, 246, 0.2);
  }
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg}px;
  margin-bottom: ${spacing.lg}px;
`;

const StatusIcon = styled.div<{ installed: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  background: ${props => props.installed
    ? 'rgba(40, 167, 69, 0.1)'
    : 'rgba(255, 193, 7, 0.1)'};
  border: 2px solid ${props => props.installed
    ? colors.truthGreen
    : '#FFC107'};
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.h3`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.softWhite};
  margin-bottom: ${spacing.xs}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const StatusText = styled.p<{ installed: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  color: ${props => props.installed ? colors.truthGreen : '#FFC107'};
  margin: 0;
`;

const InstallButton = styled.button`
  width: 100%;
  padding: ${spacing.md}px ${spacing.xl}px;
  background: ${colors.honestTeal};
  border: none;
  border-radius: 8px;
  color: ${colors.softWhite};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.deepTrustBlue};
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 191, 166, 0.3);
  }
`;

const ControlCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 191, 166, 0.2);
  border-radius: 16px;
  padding: ${spacing['2xl']}px;
  margin-bottom: ${spacing.xl}px;

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.02);
    border-color: rgba(139, 92, 246, 0.2);
  }
`;

const ControlHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.md}px;
`;

const ControlTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${spacing.sm}px;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.softWhite};

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const AgentIcon = styled.span`
  font-size: ${typography.fontSize['2xl']};
`;

const ToggleSwitch = styled.label`
  position: relative;
  width: 60px;
  height: 32px;
  display: inline-block;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleSlider = styled.span<{ enabled: boolean }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.enabled
    ? colors.truthGreen
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 32px;
  transition: 0.4s;

  [data-theme="light"] & {
    background: ${props => props.enabled
      ? colors.truthGreen
      : 'rgba(0, 0, 0, 0.1)'};
  }

  &:before {
    position: absolute;
    content: "";
    height: 24px;
    width: 24px;
    left: 4px;
    bottom: 4px;
    background: ${colors.white};
    border-radius: 50%;
    transition: 0.4s;
    transform: ${props => props.enabled ? 'translateX(28px)' : 'translateX(0)'};
  }
`;

const ControlDescription = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  color: ${colors.steelGray};
  margin: 0;
`;

const GuideSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 191, 166, 0.2);
  border-radius: 16px;
  padding: ${spacing['2xl']}px;
  margin-bottom: ${spacing.xl}px;

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.02);
    border-color: rgba(139, 92, 246, 0.2);
  }
`;

const GuideHeader = styled.div`
  margin-bottom: ${spacing.xl}px;
`;

const GuideTitle = styled.h3`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.softWhite};
  margin-bottom: ${spacing.sm}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const GuideSubtitle = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  color: ${colors.steelGray};
  margin: 0;
`;

const VideoGuide = styled.div`
  margin-bottom: ${spacing['2xl']}px;
`;

const VideoPlaceholder = styled.div`
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, rgba(0, 82, 204, 0.1) 0%, rgba(0, 191, 166, 0.1) 100%);
  border: 2px solid rgba(0, 191, 166, 0.3);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md}px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.honestTeal};
    transform: scale(1.02);
  }
`;

const PlayIcon = styled.div`
  font-size: 64px;
  opacity: 0.8;
`;

const VideoTitle = styled.h4`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.softWhite};
  margin: 0;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const VideoDescription = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.steelGray};
  text-align: center;
  max-width: 500px;
  margin: 0;
`;

const GuideSteps = styled.div`
  display: grid;
  gap: ${spacing.lg}px;
`;

const Step = styled.div`
  display: flex;
  gap: ${spacing.lg}px;
  align-items: start;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${colors.honestTeal};
  color: ${colors.softWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h5`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.softWhite};
  margin-bottom: ${spacing.xs}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const StepText = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  color: ${colors.steelGray};
  line-height: ${typography.lineHeight.relaxed};
  margin: 0;
`;

const ActivitySection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 191, 166, 0.2);
  border-radius: 16px;
  padding: ${spacing['2xl']}px;
  animation: fadeIn 0.5s ease-out;

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.02);
    border-color: rgba(139, 92, 246, 0.2);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.xl}px;
`;

const ActivityTitle = styled.h3`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.softWhite};
  margin: 0;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const ActivityBadge = styled.div`
  padding: ${spacing.xs}px ${spacing.md}px;
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid ${colors.truthGreen};
  border-radius: 12px;
  color: ${colors.truthGreen};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md}px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  padding: ${spacing.lg}px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(0, 191, 166, 0.3);
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.02);
    border-color: rgba(139, 92, 246, 0.1);

    &:hover {
      background: rgba(139, 92, 246, 0.05);
      border-color: rgba(139, 92, 246, 0.3);
    }
  }
`;

const ActivityIcon = styled.div`
  font-size: ${typography.fontSize['2xl']};
`;

const ActivityInfo = styled.div`
  flex: 1;
`;

const ActivityText = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  color: ${colors.softWhite};
  margin-bottom: ${spacing.xs}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const ActivityTime = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.steelGray};
  margin: 0;
`;

const ActivityResult = styled.div<{ type: 'safe' | 'warning' }>`
  padding: ${spacing.xs}px ${spacing.md}px;
  background: ${props => props.type === 'safe'
    ? 'rgba(40, 167, 69, 0.1)'
    : 'rgba(255, 193, 7, 0.1)'};
  border: 1px solid ${props => props.type === 'safe'
    ? colors.truthGreen
    : '#FFC107'};
  border-radius: 8px;
  color: ${props => props.type === 'safe' ? colors.truthGreen : '#FFC107'};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  white-space: nowrap;
`;
